import { Box, Button, Link, Paper, Stack, Typography } from "@mui/material";
import { assertText, ConversationMessage, datetimeSimplifier } from "../../../redux/conversation/conversationSlice";
import { MessageType } from "../../../redux/conversation/messageListSlice";
import { getBackendPDFUrl } from "../../../redux/common";
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FileInfo } from "../../../redux/file/fileSlice";
import { SurveyInfo, SurveyType } from "../../../redux/survey/surveySlice";

export interface MessageContentProps {
    message: ConversationMessage;
    content?: React.ReactNode;
    align: "left" | "right";
}

export const MessageContent = (props: MessageContentProps) => {
    const msg = props.message;
    const align = props.align;

    if (msg.messageType === MessageType.PDF || msg.messageType === MessageType.FILE) {
        return doFile();
    } else if (msg.messageType === MessageType.SURVEY) {
        return buildSurvey();
    } else if (props.content) {
        return <>{props.content}</>;
    } else {
        return <>{assertText(msg.text)}</>;
    }

    function buildSurvey() {
        const survey: SurveyInfo = JSON.parse(`${msg.text}`);
        return <Box sx={{ minWidth: 300, my: 1 }}>
            {survey.questions &&
                survey.questions.map((question, index) => (
                    <Paper key={`${survey.id}-question-${index}`} elevation={10} sx={{ padding: "10px", marginBottom: "10px", backgroundColor: "#f5f5f5" }}>
                        <Typography variant="h6" sx={{ marginTop: "0px" }}>
                            {question.title}
                        </Typography>
                        <Typography variant="body1" sx={{ marginTop: "0px", marginBottom: "0px" }}>
                            {assertText(question.description)}
                        </Typography>
                        {survey.questions && survey.questions[0] && survey.questions[0].options &&
                            survey.questions[0].options.map((option, index) => (
                                <Button key={`preview-option-${index}`} disabled={true} variant="outlined" fullWidth sx={{ mb: 1, textTransform: "none" }}>
                                    {option.title}
                                </Button>
                            ))
                        }

                    </Paper>

                ))}
                <Typography variant="body2" sx={{ marginTop: "0px", fontSize: "10px", fontStyle: "italic",
                    color: (theme) => survey.type === SurveyType.PRIVATE_PER_STUDENT ? theme.palette.text.primary : theme.palette.text.secondary
                }}>
                    Um voto por {survey.type === SurveyType.PRIVATE_PER_STUDENT ? "aluno" : "usuário"}
                </Typography>
                {
                    survey.endAt &&
                    <Typography variant="body2" sx={{ marginTop: "0px", fontSize: "10px", fontStyle: "italic",
                        color: (theme) => theme.palette.text.secondary
                    }}>
                        Válido até: {datetimeSimplifier(survey.endAt)}
                    </Typography>
                }

        </Box>;
    }

    function doFile() {
        let fileInfo: undefined | FileInfo = undefined;
        if (msg.text) {
            fileInfo = JSON.parse(msg.text);
        }

        const elA = <Link href={`${getBackendPDFUrl(msg.contentId)}`} target="_blank" rel="noopener noreferrer"
            underline="none" color="inherit" sx={{ textDecoration: 'none', alignContent: 'center' }}>
            {msg.messageType === MessageType.PDF
                ? <OpenInNewIcon fontSize="large" sx={{ verticalAlign: 'middle', m: '0' }} />
                : <DownloadIcon fontSize="large" sx={{ verticalAlign: 'middle', m: '0' }} />}
        </Link>;
        return (
            <Box sx={{ mx: 1 }}>
                <Stack direction="row" spacing={"4px"} sx={{ marginBottom: 1 }}>
                    {align == "left" && elA}
                    <Stack direction="column" alignItems={align == "left" ? "self-start" : "self-end"} spacing={0}
                        sx={{ marginBottom: 1, minWidth: "250px", maxWidth: "600px" }}>
                        <Typography variant="body1" component="span" color='black'
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "block", textWrap: "nowrap" }}>
                            {fileInfo?.fileName ?? "Arquivo PDF"}
                        </Typography>
                        <Typography variant="body2" component="span" color='black'
                            fontSize={10}
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "block", textWrap: "nowrap" }}>
                            {msg.messageType === MessageType.PDF ? "Arquivo PDF" : "Arquivo"} -
                            Tamanho: {assertSize(fileInfo?.size) ?? "-"}
                        </Typography>
                    </Stack>
                    {align != "left" && elA}
                </Stack>
            </Box>
        );
    }
};

function assertSize(size: number | undefined): string | undefined {
    if (size === undefined) {
        return undefined;
    }
    if (size < 1024) {
        return `${size} B`;
    } else if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    } else if (size < 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    } else {
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
}